import * as React from "react"
import { useTranslation } from "react-i18next"
import { CheckCircle2, Download, Share2, ThumbsDown, ThumbsUp } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { COMPOSER_BOX_CLASS, composerRows } from "@/lib/composer-size"
import { endpoints } from "@/lib/endpoints"
import { ApiError, isApiError, publicBlob, publicPost } from "@/lib/api"
import { useFormatDay } from "@/lib/dates"
import { formatPaise, formatRupees } from "@/lib/money"
import { createObjectUrl, revokeObjectUrl } from "@/lib/pdf"
import { APPROVAL_LINK_REFUSED, type ApprovalPortalView } from "@steelbill/types"

/**
 * §17.1 — THE CUSTOMER APPROVAL PORTAL. One page, three doors into it.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE OWNER ASKED FOR THREE WAYS IN — a link by email, a link by WhatsApp, and a
 * QR printed on the bottom right of the quotation — AND ONE PAGE BEHIND ALL
 * THREE. This is that page. It is built once, and the three doors differ only in
 * how the token got into the address bar.
 *
 * IT IS THE ONLY SCREEN IN THIS APP WITH NO SESSION BEHIND IT. Read it as
 * customer-facing software, not as part of the billing app:
 *
 *  - IT IS OUTSIDE `ProtectedRoute` AND OUTSIDE `AppShell`. No sidebar, no
 *    bell, no search, no nav. A customer must not be shown the furniture of a
 *    system they have no account on, and `AppShell` calls hooks that assume one.
 *  - IT ASKS THE SERVER FOR EVERYTHING AND COMPUTES NOTHING. Every figure is
 *    integer paise from the API, printed through `formatPaise` / `formatRupees`
 *    (CLAUDE.md — Indian digit grouping, never `toLocaleString`). The totals
 *    block is the order the SERVER resolved (§9.3): under the default TAX_EXTRA
 *    the tax rows print the word EXTRA and the headline figure is PRE-TAX, which
 *    is what the attached sheet says. A portal that re-derived a total would be
 *    a second tax engine, on the internet, with no session.
 *  - IT IS READ BY SOMEBODY WHO IS NOT THE OWNER, so every string is translated
 *    (§11.4) and every sentence is written for a purchasing clerk who has never
 *    heard of this software.
 *
 * THE TOKEN LIVES IN THE URL FRAGMENT (`/q#<token>`) and is read from
 * `location.hash`. A fragment is never sent to a server on a request line, so it
 * reaches no nginx log and no `Referer`; this component puts it in a POST body
 * and nowhere else. It is deliberately NOT held in a query string, NOT written
 * to `localStorage`, and NOT echoed back by the API.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** What the page is doing, as one value — no boolean soup. */
type Phase =
  | { kind: "loading" }
  | { kind: "refused" }
  | { kind: "error"; message: string }
  | { kind: "ready"; view: ApprovalPortalView }

/**
 * The token, out of the fragment.
 *
 * `location.hash` includes the leading `#`. A missing or empty fragment is the
 * same as a bad one — somebody typed `/q` by hand, or a mail client stripped the
 * fragment — and gets the same refusal, because there is no useful distinction
 * to draw for the reader and drawing one would be a distinction an attacker
 * could also read.
 */
function tokenFromHash(): string {
  if (typeof window === "undefined") return ""
  return window.location.hash.replace(/^#/, "").trim()
}

export function ApprovalPortalScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()

  const [phase, setPhase] = React.useState<Phase>({ kind: "loading" })
  const [name, setName] = React.useState("")
  const [note, setNote] = React.useState("")
  const [busy, setBusy] = React.useState<null | "APPROVED" | "REJECTED" | "download">(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  /** Decided in THIS session — see the note where it is set. */
  const [justDecided, setJustDecided] = React.useState(false)

  const token = React.useMemo(tokenFromHash, [])

  /*
   * ONE LOAD, ON MOUNT. Not a React Query key, deliberately: a query key is a
   * CACHE key, and the only thing available to key on here is the token — which
   * would put a bearer credential into the query cache, into any devtools panel
   * the owner has open, and into whatever a future persister writes to
   * IndexedDB. The page loads once and re-reads only after a decision, which it
   * already has the answer to.
   */
  React.useEffect(() => {
    let cancelled = false

    if (token === "") {
      setPhase({ kind: "refused" })
      return
    }

    void publicPost<{ data: ApprovalPortalView }>(endpoints.publicApprovalView, { token })
      .then((response) => {
        if (!cancelled) setPhase({ kind: "ready", view: response.data })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setPhase(describeFailure(error, t))
      })

    return () => {
      cancelled = true
    }
  }, [token, t])

  /* ---------------------------------------------------------------------- */
  /* Download — and the WhatsApp share the owner asked for                    */
  /* ---------------------------------------------------------------------- */

  /**
   * Fetch the PDF and hand it to the platform.
   *
   * THE BYTES COME THROUGH THE API AND BECOME AN OBJECT URL, for the reason
   * `lib/pdf.ts` gives about every other document in this app: there is no
   * navigable URL that returns this file, because the token is not in one.
   *
   * `share` IS THE OWNER'S "send pdf via whats app". `navigator.share` with a
   * File hands the PDF to whatever the customer's phone offers — WhatsApp among
   * them — which is the ONLY way a web page can put a file into WhatsApp. There
   * is deliberately no server endpoint for it: "send this document to a number I
   * choose" on an unauthenticated surface would be an open relay. When the
   * platform cannot share a file, the button downloads instead, which is the
   * same two-step the billing app's own share control falls back to.
   */
  async function fetchPdf(mode: "download" | "share"): Promise<void> {
    if (phase.kind !== "ready") return
    setBusy("download")
    setActionError(null)
    try {
      const file = await publicBlob(
        endpoints.publicApprovalPdf,
        { token },
        `${phase.view.number.replace(/\//g, "-")}.pdf`,
      )

      const asFile = new File([file.blob], file.filename, { type: file.contentType })
      const canShare =
        mode === "share" &&
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [asFile] })

      if (canShare) {
        await navigator.share({ files: [asFile], title: phase.view.number })
        return
      }

      const url = createObjectUrl(file.blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = file.filename
      anchor.rel = "noopener"
      anchor.click()
      // Revoked on the next tick: the download has started by then, and holding
      // it would leak the whole PDF into memory on a phone.
      window.setTimeout(() => revokeObjectUrl(url), 0)
    } catch (error: unknown) {
      // `AbortError` is the customer dismissing the share sheet. Not a failure.
      if ((error as { name?: string } | null)?.name === "AbortError") return
      setActionError(describeFailure(error, t).kind === "refused" ? t("approval.refused") : t("approval.downloadFailed"))
    } finally {
      setBusy(null)
    }
  }

  /* ---------------------------------------------------------------------- */
  /* The decision                                                            */
  /* ---------------------------------------------------------------------- */

  async function decide(decision: "APPROVED" | "REJECTED"): Promise<void> {
    if (phase.kind !== "ready") return
    if (name.trim() === "") {
      setActionError(t("approval.nameRequired"))
      return
    }

    setBusy(decision)
    setActionError(null)
    try {
      const response = await publicPost<{ data: ApprovalPortalView }>(
        endpoints.publicApprovalDecision,
        {
          token,
          decision,
          name: name.trim(),
          ...(note.trim() === "" ? {} : { note: note.trim() }),
        },
      )
      // The SERVER's view of the quotation replaces ours wholesale — including
      // `canDecide`, so the buttons disappear because the server said so and
      // not because this component guessed.
      setPhase({ kind: "ready", view: response.data })
      /*
       * ANSWERING IS NOT THE SAME AS COMING BACK TO AN ANSWERED PAGE.
       *
       * A customer who has just pressed Approve should be thanked and shown what
       * they agreed to; "this quotation has already been approved, nothing is
       * pending" is what you say to somebody arriving at a stale link, and
       * reading it one second after deciding is cold and faintly accusatory.
       *
       * So the minimal receipt is for a REVISIT. This flag says the decision
       * happened in this session, and it lives only in component state — a
       * reload is a revisit, which is exactly right.
       */
      setJustDecided(true)
    } catch (error: unknown) {
      const failure = describeFailure(error, t)
      if (failure.kind === "refused") {
        setPhase(failure)
        return
      }
      setActionError(failure.kind === "error" ? failure.message : t("approval.decisionFailed"))
    } finally {
      setBusy(null)
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Render                                                                  */
  /* ---------------------------------------------------------------------- */

  if (phase.kind === "loading") {
    return (
      <Centred>
        <Spinner className="size-8" />
        <p className="text-muted-foreground text-sm">{t("approval.loading")}</p>
      </Centred>
    )
  }

  /**
   * THE REFUSAL PAGE, AND IT IS A SENTENCE A CUSTOMER UNDERSTANDS.
   *
   * §17.1 is explicit that a scanned sheet from six months ago must not produce
   * a 404. Every refusal — expired, tampered, truncated, unknown, or a quotation
   * that was called off — lands here with the SAME words, because the server
   * gives them the same answer for the same reason (an oracle is an oracle even
   * when it is rendered politely). What the reader needs is the next action, and
   * it is the same in every case: ask the business for a current copy.
   */
  if (phase.kind === "refused") {
    return (
      <Centred>
        <Alert>
          <AlertTitle>{t("approval.refusedTitle")}</AlertTitle>
          <AlertDescription>{t("approval.refused")}</AlertDescription>
        </Alert>
      </Centred>
    )
  }

  if (phase.kind === "error") {
    return (
      <Centred>
        <Alert variant="destructive">
          <AlertTitle>{t("approval.errorTitle")}</AlertTitle>
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
      </Centred>
    )
  }

  const { view } = phase

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * NOTHING LEFT TO DECIDE — A RECEIPT, NOT THE QUOTATION (2026-08-20).
   *
   * Answered, lapsed or cancelled: the page says so and stops. It does NOT go on
   * to print the line items, the rates and the totals under a greyed-out button.
   *
   * THE THING WORTH PROTECTING IS THE PRICING. This link is a bearer credential
   * that cannot be recalled — it lives in a forwarded email, a WhatsApp group, a
   * photographed sheet — and it stays valid until it expires, which for a
   * printed QR can be a year. Leaving the full quotation behind it means a rate
   * card sitting on a public URL long after the job it priced was settled.
   * Answering "did that go through?" needs none of that.
   *
   * NO DOWNLOAD EITHER, and that is the same decision rather than a separate
   * one: leaving it would put the pricing one click away and make the rest of
   * this decoration. The customer was emailed the PDF when it was sent; if they
   * have lost it, the page tells them to get in touch, which is a phone call the
   * owner would rather have than a price list living behind a stale link.
   *
   * THE SERVER ALREADY REFUSES THE DECISION ITSELF (`decideBlocker` → DECIDED /
   * LAPSED / CLOSED, and `canDecide` false), so this changes what is SHOWN and
   * nothing about what can be done. It is a privacy improvement, not a hole
   * being closed.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  if (!view.canDecide && !justDecided) {
    const heading =
      view.cannotDecideReason === "DECIDED"
        ? view.decision === "REJECTED"
          ? t("approval.settledRejected")
          : t("approval.settledApproved")
        : view.cannotDecideReason === "LAPSED"
          ? t("approval.settledLapsed")
          : t("approval.settledClosed")

    return (
      <main className="bg-muted/30 min-h-dvh px-4 py-6 sm:px-6 sm:py-10">
        <div className="mx-auto flex w-full max-w-xl flex-col gap-4">
          <header className="text-center">
            <h1 className="text-xl font-semibold sm:text-2xl">{view.seller.tradeName}</h1>
          </header>

          <Card>
            <CardHeader>
              <CardTitle>{heading}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <p className="text-muted-foreground text-sm">{t("approval.settledBody")}</p>

              {/* WHICH quotation — somebody with two open offers needs to know
                  which one this was. The number is on the sheet they hold. */}
              <p className="text-sm">{t("approval.title", { number: view.number })}</p>

              {/* The same contact already printed on every document. */}
              <p className="text-muted-foreground text-sm">
                {[view.seller.tradeName, view.seller.phone, view.seller.email]
                  .filter((part) => part !== null && part !== "")
                  .join(" · ")}
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    )
  }

  return (
    <main className="bg-muted/30 min-h-dvh px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
        {/* The letterhead, and nothing more of the seller than the sheet already
            carries. No GSTIN, no PAN, no bank details — see the API's
            `portalSeller`. */}
        <header className="text-center">
          <h1 className="text-xl font-semibold sm:text-2xl">{view.seller.tradeName}</h1>
          {view.seller.addressLine !== "" && (
            <p className="text-muted-foreground text-sm">{view.seller.addressLine}</p>
          )}
        </header>

        <Card>
          <CardHeader>
            <CardTitle>{t("approval.title", { number: view.number })}</CardTitle>
            <dl className="text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-1 pt-2 text-sm">
              <dt>{t("approval.dateLabel")}</dt>
              <dd className="text-foreground text-right">{formatDay(view.date)}</dd>
              <dt>{t("approval.toLabel")}</dt>
              <dd className="text-foreground text-right">{view.customerName}</dd>
              {view.attnName !== null && (
                <>
                  <dt>{t("approval.attnLabel")}</dt>
                  <dd className="text-foreground text-right">{view.attnName}</dd>
                </>
              )}
              {view.validUntil !== null && (
                <>
                  <dt>{t("approval.validUntilLabel")}</dt>
                  <dd className="text-foreground text-right">{formatDay(view.validUntil)}</dd>
                </>
              )}
            </dl>
          </CardHeader>

          <CardContent className="flex flex-col gap-4">
            {view.subject !== null && <p className="font-medium">{view.subject}</p>}

            {/* The lines. A horizontal scroll container of its own, so a narrow
                phone never scrolls the whole page sideways. */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[32rem] text-sm">
                <thead className="text-muted-foreground border-b text-left">
                  <tr>
                    <th className="w-10 py-2 font-normal">{t("approval.colNo")}</th>
                    <th className="py-2 font-normal">{t("approval.colDescription")}</th>
                    <th className="py-2 text-right font-normal">{t("approval.colQty")}</th>
                    <th className="py-2 text-right font-normal">{t("approval.colRate")}</th>
                    <th className="py-2 text-right font-normal">{t("approval.colAmount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {view.lines.map((line) => (
                    // The PRINTED label as the key — there is no id on this page
                    // by design, and `lineNo` is unique on a document.
                    <tr key={line.lineNo} className="border-b last:border-0">
                      <td className="py-2 align-top tabular-nums">{line.lineNo}</td>
                      <td className="py-2 align-top whitespace-pre-line">{line.description}</td>
                      <td className="py-2 text-right align-top tabular-nums">{line.quantity}</td>
                      <td className="py-2 text-right align-top tabular-nums">
                        {formatPaise(line.ratePaise)}
                      </td>
                      <td className="py-2 text-right align-top tabular-nums">
                        {formatPaise(line.amountPaise)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Separator />

            {/* THE SERVER'S TOTALS, IN THE SERVER'S ORDER. Under TAX_EXTRA the
                tax rows carry the word EXTRA and the emphasised row is the
                PRE-TAX figure — the same one on the attached sheet (§9.3). */}
            <dl className="ml-auto grid w-full max-w-xs grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {view.totals.map((row) => (
                <React.Fragment key={row.label}>
                  <dt className={row.emphasis === true ? "font-semibold" : "text-muted-foreground"}>
                    {row.label}
                  </dt>
                  <dd
                    className={`text-right tabular-nums ${row.emphasis === true ? "font-semibold" : ""}`}
                  >
                    {row.amountPaise === null
                      ? (row.text ?? "")
                      : row.emphasis === true
                        ? formatRupees(row.amountPaise)
                        : formatPaise(row.amountPaise)}
                  </dd>
                </React.Fragment>
              ))}
            </dl>

            {view.taxIsExtra && (
              <p className="text-muted-foreground text-right text-xs">{t("approval.taxExtraNote")}</p>
            )}

            {(view.terms !== null || view.notes !== null) && (
              <div className="text-muted-foreground flex flex-col gap-2 text-xs">
                {view.terms !== null && <p className="whitespace-pre-line">{view.terms}</p>}
                {view.notes !== null && <p className="whitespace-pre-line">{view.notes}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* The three actions the owner asked for: download, share onward, decide. */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void fetchPdf("download")} disabled={busy !== null}>
            <Download aria-hidden="true" />
            {t("approval.download")}
          </Button>
          <Button variant="outline" onClick={() => void fetchPdf("share")} disabled={busy !== null}>
            <Share2 aria-hidden="true" />
            {t("approval.shareOn")}
          </Button>
        </div>

        {actionError !== null && (
          <Alert variant="destructive">
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        )}

        {view.canDecide ? (
          <Card>
            <CardHeader>
              <CardTitle>{t("approval.decideTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {/*
                THE NAME IS REQUIRED, AND THE HELP TEXT SAYS WHY IN THE READER'S
                TERMS. Nobody signed in, so this is the whole record of who
                answered — the server stores it beside the instant and the
                address. The server enforces it too; this only saves a round
                trip.
              */}
              <div className="flex flex-col gap-2">
                <Label htmlFor="approval-name">{t("approval.nameLabel")}</Label>
                <Input
                  id="approval-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={191}
                  autoComplete="name"
                  placeholder={t("approval.namePlaceholder")}
                />
                <p className="text-muted-foreground text-xs">{t("approval.nameHelp")}</p>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="approval-note">{t("approval.noteLabel")}</Label>
                {/*
                  THE SAME COMPOSE BOX AS THE MESSENGER'S (owner's ask,
                  2026-08-20: "also for quotation send"). This is the one box on
                  the QUOTATION SEND loop that a person types a message into —
                  `POST /quotations/:id/email` has no message field at all, by
                  design: `QuotationEmailSchema` is a strict empty object and the
                  server owns the recipient, the subject and the body (§6, §7).
                  So "the write box, also for quotation send" lands here, on the
                  reply the customer types back.

                  One line to start, growing with what is typed, capped so a
                  pasted specification cannot push Approve and Reject off a phone
                  screen — which on THIS screen would leave a customer holding an
                  offer he cannot answer. `lib/composer-size.ts` owns both halves.
                */}
                <Textarea
                  id="approval-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  maxLength={1000}
                  rows={composerRows(note)}
                  className={COMPOSER_BOX_CLASS}
                  placeholder={t("approval.notePlaceholder")}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void decide("APPROVED")} disabled={busy !== null}>
                  <ThumbsUp aria-hidden="true" />
                  {t("approval.approve")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void decide("REJECTED")}
                  disabled={busy !== null}
                >
                  <ThumbsDown aria-hidden="true" />
                  {t("approval.reject")}
                </Button>
              </div>

              <p className="text-muted-foreground text-xs">{t("approval.decideOnce")}</p>
            </CardContent>
          </Card>
        ) : (
          <Alert>
            {view.decision !== null && <CheckCircle2 aria-hidden="true" />}
            <AlertTitle>
              {view.decision === "APPROVED"
                ? t("approval.alreadyApproved")
                : view.decision === "REJECTED"
                  ? t("approval.alreadyRejected")
                  : t("approval.closedTitle")}
            </AlertTitle>
            <AlertDescription>
              {view.decision !== null
                ? t("approval.alreadyDecidedBody", {
                    name: view.decidedByName ?? "",
                    date: view.decidedAt === null ? "" : formatDay(view.decidedAt.slice(0, 10)),
                  })
                : view.cannotDecideReason === "LAPSED"
                  ? t("approval.lapsedBody")
                  : t("approval.closedBody")}
            </AlertDescription>
          </Alert>
        )}

        {/* The way back to a human, on every state of this page — it is the
            action every refusal and every "already answered" points at. */}
        <footer className="text-muted-foreground text-center text-xs">
          {t("approval.contact", {
            phone: view.seller.phone ?? "",
            email: view.seller.email ?? "",
          })}
        </footer>
      </div>
    </main>
  )
}

/** The frame the three non-document states share. */
function Centred({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <main className="bg-muted/30 flex min-h-dvh items-center justify-center px-4">
      <div className="flex w-full max-w-md flex-col items-center gap-3 text-center">{children}</div>
    </main>
  )
}

/**
 * Turn a thrown value into a phase.
 *
 * `APPROVAL_LINK_REFUSED` IS A KEY, NOT A SENTENCE — the API sends the same one
 * for every bad token precisely so this component can render it in the reader's
 * own language (§11.4) without the server having to know which. A 404 means the
 * feature is switched off (§18.2) and gets the SAME refusal page: from the
 * customer's side "there is no such page here" and "this link no longer works"
 * are the same event with the same next action, and telling them apart would
 * announce that a portal exists on this host and is merely disabled.
 */
function describeFailure(error: unknown, t: (key: string) => string): Phase {
  if (isApiError(error)) {
    const api = error as ApiError
    if (api.message === APPROVAL_LINK_REFUSED) return { kind: "refused" }
    if (api.status === 410 || api.status === 404) return { kind: "refused" }
    if (api.status === 409) return { kind: "error", message: api.message }
    return { kind: "error", message: api.message }
  }
  return { kind: "error", message: t("approval.errorBody") }
}
